import React, { FunctionComponent } from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import * as R from 'ramda';

import { CheckboxGroup } from 'src/components/molecules/CheckboxGroup';
import { Filter, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { FilterOptionsProps } from '../../types';
import { FilterOptionsExpandableContent } from './FilterOptionsExpandableContent';
import { getActiveFilterCountString } from './utils';

const getSelectedCheckboxValues = (
  selectedFilterOptions: SelectedFilterOption[],
  filterUid?: string,
) => {
  // Note: using selectedFilterOptions to store value, not active flag
  const selectedFilterOption: SelectedFilterOption | undefined = R.find<SelectedFilterOption>(
    R.propEq('filterUid', filterUid),
  )(selectedFilterOptions);

  const selectedCheckboxValuesUncast: FilterOptionValue[] =
    (selectedFilterOption?.filterOptionValue as FilterOptionValue[]) ?? [];

  const selectedCheckboxValues = R.map(
    (selectedCheckboxValueUncast) => (selectedCheckboxValueUncast as string | number).toString(),
    selectedCheckboxValuesUncast,
  );

  return selectedCheckboxValues;
};

export const onChange = (
  checkboxValue: string[],
  filter: Filter,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) =>
  onSelectFilterOption({
    filterUid: filter.uid,
    filterOptionValue: checkboxValue,
  });

interface Props extends Omit<FilterOptionsProps, 'currencyCode'> {
  isExpanded?: boolean;
}

export const CheckboxGroupWrapper: FunctionComponent<Props> = ({
  filter,
  isExpanded = true,
  selectedFilterOptions,
  onSelectFilterOption,
  onExpandableContentToggle,
}: Props) => {
  const breakpoint = useBreakpoint();
  const isExpandable = filter.options.length > 5 && breakpoint !== Breakpoint.XXS;

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable && isExpanded}
    >
      <CheckboxGroup
        name={filter.uid}
        accessibleLabel={filter.title}
        value={getSelectedCheckboxValues(selectedFilterOptions, filter.uid)}
        onChange={(checkboxValue: string[]) =>
          onChange(checkboxValue, filter, onSelectFilterOption)
        }
        checkboxData={filter.options.map(({ title, value, activeCount }) => ({
          labelText: title,
          value: value as string,
          secondaryLabelText: getActiveFilterCountString(activeCount),
        }))}
        hideLabel
      />
    </FilterOptionsExpandableContent>
  );
};
